<template>
  <a-layout class="admin-shell">
    <a-layout-sider
      v-model:collapsed="collapsed"
      :width="252"
      collapsible
      :theme="darkMode ? 'dark' : 'light'"
      class="admin-sider"
    >
      <div class="admin-brand">
        <BrandLogo :variant="collapsed ? 'mark' : 'zh-primary'" tone="current" />
        <small v-if="!collapsed">管理控制台</small>
      </div>
      <a-menu
        :selected-keys="selectedKeys"
        :open-keys="openKeys"
        mode="inline"
        :items="menuItems"
        @openChange="openGroup"
        @click="navigate"
      />
      <template #trigger
        ><i :class="collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'"
      /></template>
    </a-layout-sider>
    <a-layout>
      <a-layout-header class="admin-header">
        <div v-if="!route.meta.hidePageTitle" class="admin-header__context">
          <h1>{{ title }}</h1>
          <span>{{ description }}</span>
        </div>
        <div class="admin-header__actions">
          <a-tag color="purple"><i class="ri-shield-check-line" />安全会话</a-tag
          ><a-button
            type="text"
            :title="`切换到${darkMode ? '浅色' : '深色'}模式`"
            @click="toggleTheme"
            ><i :class="darkMode ? 'ri-sun-line' : 'ri-moon-line'" /></a-button
          ><a-divider type="vertical" /><a-dropdown
            ><a-button type="text" class="admin-profile"
              ><i class="ri-user-settings-line" /><span>{{
                session.profile?.displayName ?? '管理员'
              }}</span
              ><i class="ri-arrow-down-s-line" /></a-button
            ><template #overlay
              ><a-menu
                ><a-menu-item key="logout" @click="logout">退出登录</a-menu-item></a-menu
              ></template
            ></a-dropdown
          >
        </div>
      </a-layout-header>
      <a-layout-content class="admin-content">
        <RouterView />
      </a-layout-content>
    </a-layout>
  </a-layout>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { useRoute, useRouter } from 'vue-router';
import { adminApi, setAdminToken } from '@/api';
import { adminNavigation, toMenuItems } from '@/navigation';
import { useAdminSessionStore } from '@/stores/admin-session.store';
import { useMessageReviewStore } from '@/stores/message-review.store';
import BrandLogo from '@/components/BrandLogo.vue';
import {
  getLocalTheme,
  resolveTheme,
  saveLocalTheme,
  type LocalTheme,
} from '../../../design-system/theme';

const router = useRouter();
const route = useRoute();
const session = useAdminSessionStore();
const messageReviews = useMessageReviewStore();
const collapsed = ref(false);
const localTheme = ref<LocalTheme>(getLocalTheme('ai-workspace.admin.theme'));
const darkMode = ref(resolveTheme(localTheme.value) === 'dark');
const selectedKeys = computed(() => [String(route.name)]);
const menuItems = computed(() =>
  toMenuItems((permission) => session.can(permission), messageReviews.summary.pending),
);
const currentGroupKey = computed(
  () =>
    adminNavigation.find((group) => group.children.some((item) => item.routeName === route.name))
      ?.key,
);
const openKeys = ref(currentGroupKey.value ? [currentGroupKey.value] : []);
watch(currentGroupKey, (key) => {
  openKeys.value = key ? [key] : [];
});
const currentItem = computed(() =>
  adminNavigation.flatMap((group) => group.children).find((item) => item.routeName === route.name),
);
const section = computed(
  () =>
    adminNavigation.find((group) => group.children.some((item) => item.routeName === route.name))
      ?.label ?? '运营概览',
);
const title = computed(() => currentItem.value?.label ?? '数据看板');
const description = computed(
  () =>
    (
      ({
        数据看板: '查看用户增长、营销转化与交易经营数据。',
        用户: '管理账号状态、套餐和工作台归属。',
        工作台: '查看跨租户工作台的生命周期与访问状态。',
        审核队列: '审核固定发布版本，确保上线内容可追溯。',
        资产管理: '管理已提交能力资产的质量与运营优先级。',
        专题运营: '将已审核资产编排到商店的主题运营位。',
        运营分析: '以安装就绪和有效激活衡量真实使用。',
        商品: '维护会员与额度商品；支付关闭时仍可安全配置。',
        优惠与兑换券: '配置券活动、领取规则与批量发放任务。',
        订单: '查看全站订单和支付状态。',
        退款: '处理退款申请并保留审批留痕。',
        结算: '核对发布者收入与结算状态。',
        支付流水: '追踪支付渠道请求与回调结果。',
        支付设置: '维护渠道可售规则、凭据就绪状态与交易售后政策。',
        系统监控: '查看运行数值、服务异常与任务错误记录。',
        系统服务测试: '检查服务连接、消费者心跳与实际调用证据。',
        消息审查: '异步识别风险消息；原文查看与人工结论均可审计。',
        通知记录: '核查平台通知的投递与阅读状态。',
        审计日志: '追溯管理员的敏感操作与原因。',
        管理员与角色: '管理后台账号与会话安全。',
        系统模型: '维护系统模型的连接与计费参数。',
        增长与套餐额度: '维护套餐基础额度、签到和邀请奖励规则。',
        系统配置: '查看受版本控制的平台配置。',
        服务状态: '发布全端可见的升级与故障提示，不会阻断业务。',
      }) as Record<string, string>
    )[title.value] ?? `${section.value}的日常运营管理。`,
);

function navigate({ key }: { key: string }): void {
  void router.push({ name: key });
}
function openGroup(keys: string[]): void {
  openKeys.value = keys.length ? [keys.at(-1)!] : [];
}
function toggleTheme(): void {
  localTheme.value = darkMode.value ? 'light' : 'dark';
  darkMode.value = saveLocalTheme('ai-workspace.admin.theme', localTheme.value) === 'dark';
  window.dispatchEvent(new CustomEvent('ai-admin-theme', { detail: localTheme.value }));
}
function syncResolvedTheme(event: Event): void {
  darkMode.value =
    (event as CustomEvent<{ resolved?: 'light' | 'dark' }>).detail?.resolved === 'dark';
}
function collapseForCompactViewport(): void {
  if (window.matchMedia('(max-width: 760px)').matches) collapsed.value = true;
}
onMounted(() => {
  collapseForCompactViewport();
  window.addEventListener('resize', collapseForCompactViewport);
  window.addEventListener('aw-theme-change', syncResolvedTheme);
  if (session.can('message_review.read')) void messageReviews.load();
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', collapseForCompactViewport);
  window.removeEventListener('aw-theme-change', syncResolvedTheme);
});
async function logout(): Promise<void> {
  try {
    await adminApi.logout();
  } catch {
    /* 服务端会话已经失效时仍清理本地凭证 */
  }
  setAdminToken(null);
  await router.replace({ name: 'admin-login' });
  message.success('已退出管理员后台');
}
</script>

<style scoped>
.admin-shell {
  min-height: 100vh;
}

.admin-sider {
  position: sticky;
  top: 0;
  height: 100vh;
  border-right: 1px solid var(--aw-border);
  box-shadow: 4px 0 18px rgb(23 32 51 / 5%);
}

.admin-brand {
  height: 52px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 22px;
  border-bottom: 1px solid var(--aw-border);
}

.admin-brand small {
  margin-left: 2px;
  color: var(--aw-text-tertiary);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.admin-brand :deep(.brand-logo) {
  color: var(--aw-primary);
  font-size: 17px;
}

.admin-sider :deep(.ant-menu) {
  border-inline-end: 0;
  padding: 4px 10px;
}

.admin-sider :deep(.ant-menu-submenu-title) {
  font-weight: 700;
  color: var(--aw-text-secondary);
}

.admin-sider :deep(.ant-menu-item) {
  margin-block: 3px;
  width: 100%;
  border-radius: var(--aw-radius-sm);
}

.admin-sider :deep(.ant-menu-item i),
.admin-sider :deep(.ant-menu-submenu-title i) {
  font-size: 17px;
}

.admin-sider :deep(.admin-menu-label) {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.admin-sider :deep(.admin-menu-badge) {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--aw-primary);
  color: #fff;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

.admin-sider footer {
  position: absolute;
  right: 10px;
  bottom: 16px;
  left: 10px;
}

.admin-sider footer .ant-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #656879;
}

.admin-header {
  min-height: 92px;
  height: auto;
  padding: 18px 32px;
  background: var(--aw-surface);
  border-bottom: 1px solid var(--aw-border);
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: center;
}

.admin-header__context {
  min-width: 0;
}

.admin-header h1 {
  margin: 0;
  color: var(--aw-text);
  font-size: 23px;
  line-height: 1.25;
  font-weight: 750;
}

.admin-header__context span {
  display: block;
  margin-top: 5px;
  color: var(--aw-text-secondary);
  font-size: 13px;
  line-height: 1.45;
}

.admin-header__actions {
  display: flex;
  align-items: center;
  flex: none;
}

.admin-header__actions :deep(.ant-divider) {
  height: 24px;
  border-color: var(--aw-border);
}

.admin-profile {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--aw-text-secondary);
}

.admin-content {
  padding: 24px 32px;
  overflow: auto;
}

@media (max-width: 760px) {
  .admin-header {
    min-height: 78px;
    padding: 14px 16px;
    align-items: flex-start;
  }

  .admin-header h1 {
    font-size: 19px;
  }

  .admin-header__context span {
    font-size: 12px;
  }

  .admin-header .ant-tag,
  .admin-header__actions :deep(.ant-divider) {
    display: none;
  }

  .admin-header__actions {
    padding-top: 2px;
  }

  .admin-profile span {
    display: none;
  }

  .admin-content {
    padding: 16px;
  }

  .admin-sider {
    position: sticky;
  }

  .admin-sider:not(.ant-layout-sider-collapsed) {
    box-shadow: 8px 0 30px rgba(32, 35, 65, 0.16);
  }
}
</style>
